import { useContext } from 'react'
import styled from '@emotion/styled'
import { AccountIcon } from 'shop/assets/icons'
import { useAppContent, useImages, useModal } from 'shop/hooks'
import AccountMenu from './AccountMenu'
import CloseButton from '../Modal/CloseButton'
import { Button } from 'shop/components'
import { LandingContext } from './LandingContext'
import { useNavigate } from 'react-router-dom'
import { ImageTagType } from 'shop/types'

const LandingHeaderBar = ({
  onClose,
  partnerName
}: {
  onClose: () => void
  partnerName: string | undefined
}) => {
  const { openModal, closeModal } = useModal()
  const navigate = useNavigate()
  const { inModal } = useContext(LandingContext)
  const { altLogoImage } = useAppContent()
  const isLoggedIn = localStorage.getItem('customerId') !== null

  const { getImage, handleImageError } = useImages(
    [{ imageUrl: altLogoImage }],
    ImageTagType.Standard
  )
  const handleLoginButtonClick = () => {
    if (isLoggedIn) {
      navigate('/account')
    } else {
      openModal('login')
    }
  }

  const handleCloseModal = () => {
    onClose()
    closeModal('fulfillment')
  }

  return (
    <LandingHeader id='nav-bar-header'>
      <LogoContainer>
        <Logo
          alt={`${partnerName ? partnerName + ' Logo' : 'Logo'}`}
          src={getImage('auto', 100)}
          onError={handleImageError}
        />
      </LogoContainer>
      {inModal && (
        <ModalCloseButtonContainer>
          <CloseButton handleCloseModal={handleCloseModal} />
        </ModalCloseButtonContainer>
      )}
      {!inModal && (
        <AccountContainer>
          {isLoggedIn && <AccountMenu />}
          {!isLoggedIn && (
            <CustomerAccountSection>
              <CustomerAccountButton
                onClick={() => handleLoginButtonClick()}
                size='md'
                testId='accountsButton'
                width='auto'
              >
                <AccountIcon />
                Account
              </CustomerAccountButton>
            </CustomerAccountSection>
          )}
        </AccountContainer>
      )}
    </LandingHeader>
  )
}

const LandingHeader = styled.header(({ theme }) => ({
  borderBottom: `1px solid ${theme.colors['lineColor']}`,
  padding: theme.space[3],
  display: 'flex',
  position: 'sticky',
  top: 0,
  backgroundColor: theme.colors['white'],
  zIndex: theme.zIndex['stickyHeader']
}))

export const LogoContainer = styled.div(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  WebkitTapHighlightColor: 'transparent'
}))

const Logo = styled.img(() => ({
  maxWidth: '80%',
  maxHeight: '31px'
}))

const ModalCloseButtonContainer = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '& > div': {
    position: 'unset'
  }
}))

const AccountContainer = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  WebkitTapHighlightColor: 'transparent'
}))

const CustomerAccountButton = styled((props: any) => <Button {...props} />)(
  ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    lineHeight: '16px',
    padding: '8px 16px',
    // Max width to force multi-line of CTA text
    maxWidth: '125px',

    // Viewport 4 is wide enough for a single line of CTA text
    [theme.mediaQueries.viewport4]: {
      maxWidth: 'unset'
    },

    // Bigger svg for multi-line
    '& svg': {
      height: '20px',
      width: '20px',

      [theme.mediaQueries.viewport4]: {
        height: '16px',
        width: '16px'
      }
    }
  })
)

const CustomerAccountSection = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  [theme.mediaQueries.viewport7]: {
    justifyContent: 'flex-start',
    alignItems: 'unset'
  }
}))

export default LandingHeaderBar
